import React from 'react';
import { useApp } from '../context/AppContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  Calendar,
  BarChart3,
  Search,
  LogOut,
  Shield,
  DollarSign,
  UserCog,
  Activity,
  Settings2,
  ScrollText,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { BlanketLogo } from './BlanketLogo';
import { routes } from '../routes';
import { getAdviserRoleDisplay } from '../lib/adviser-role-display';
import {
  canManageUsers,
  canViewInvoices,
  canViewOperationalAreas,
  canViewReports,
  getRoleDisplayName,
} from '../lib/permissions';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { currentUser, logout, searchQuery, setSearchQuery } = useApp();
  const location = useLocation();
  const navigate = useNavigate();

  const role = currentUser?.role;

  const navigation = [
    { name: 'Dashboard', icon: LayoutDashboard, to: routes.dashboard, activePrefix: routes.dashboard },
    ...(canViewOperationalAreas(role)
      ? [
          { name: 'Clients', icon: Users, to: routes.clients, activePrefix: routes.clients },
          { name: 'Quotes', icon: FileText, to: routes.quotes, activePrefix: routes.quotes },
          { name: 'Renewals', icon: Calendar, to: routes.renewals, activePrefix: routes.renewals },
          { name: 'Policies', icon: Shield, to: routes.policies, activePrefix: routes.policies },
        ]
      : []),
    ...(canViewInvoices(currentUser)
      ? [{ name: 'Invoices', icon: DollarSign, to: routes.invoices, activePrefix: routes.invoices }]
      : []),
    ...(canViewReports(role)
      ? [{ name: 'Reports', icon: BarChart3, to: routes.reports, activePrefix: routes.reports }]
      : []),
    ...(canManageUsers(role)
      ? [{ name: 'Users', icon: UserCog, to: routes.users, activePrefix: routes.users }]
      : []),
  ];

  const navigationItems = navigation;

  const isRouteActive = (activePrefix: string) => {
    const path = location.pathname;
    return path === activePrefix || path.startsWith(`${activePrefix}/`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation Bar */}
      <nav className="bg-card border-b border-border sticky top-0 z-50">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Brand */}
            <div className="flex flex-col items-center gap-0.5 py-1">
              <BlanketLogo className="h-8 w-auto" />
              <p className="text-xs text-muted-foreground leading-tight whitespace-nowrap">
                {getRoleDisplayName(currentUser?.role)}
              </p>
            </div>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center gap-1">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = isRouteActive(item.activePrefix);
                return (
                  <Button
                    key={item.name}
                    variant={isActive ? 'default' : 'ghost'}
                    className="gap-2"
                    asChild
                  >
                    <NavLink to={item.to} className="gap-2 inline-flex items-center">
                      <Icon className="w-4 h-4" />
                      {item.name}
                    </NavLink>
                  </Button>
                );
              })}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="hidden lg:flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search clients..."
                    className="pl-9 w-64"
                    value={searchQuery}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchQuery(value);
                      if (value && !location.pathname.startsWith(routes.clients)) {
                        navigate(routes.clients);
                      }
                    }}
                  />
                </div>
              </div>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-sm">
                      {currentUser?.firstName?.charAt(0)}
                    </div>
                    <span className="hidden sm:inline">{currentUser?.firstName} {currentUser?.lastName}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <span>{currentUser?.email}</span>
                  </DropdownMenuItem>
                  {currentUser?.role === 'ADVISER' && (
                    <DropdownMenuItem disabled className="opacity-100 focus:bg-transparent">
                      <span className="text-xs text-muted-foreground">
                        Adviser Role: {getAdviserRoleDisplay(currentUser?.adviserRole).label}
                      </span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem>Profile Settings</DropdownMenuItem>
                  {canManageUsers(role) && (
                    <DropdownMenuItem onClick={() => navigate(routes.auditLog)}>
                      <ScrollText className="w-4 h-4 mr-2" />
                      Audit Log
                    </DropdownMenuItem>
                  )}
                  {currentUser?.role === 'SUPER_ADMIN' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate(routes.health)}>
                        <Activity className="w-4 h-4 mr-2" />
                        Health
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(routes.appConfig)}>
                        <Settings2 className="w-4 h-4 mr-2" />
                        App Config
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled className="opacity-100 focus:bg-transparent cursor-default">
                    <span className="text-xs text-muted-foreground">v{import.meta.env.VITE_DD_VERSION ?? 'dev'}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <div className="md:hidden bg-card border-b border-border px-4 py-2">
        <div className="flex gap-1 overflow-x-auto">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = isRouteActive(item.activePrefix);
            return (
              <Button
                key={item.name}
                variant={isActive ? 'default' : 'ghost'}
                size="sm"
                className="gap-2 flex-shrink-0"
                asChild
              >
                <NavLink to={item.to} className="gap-2 inline-flex items-center">
                  <Icon className="w-4 h-4" />
                  {item.name}
                </NavLink>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <main className="px-4 sm:px-6 lg:px-8 py-6">
        {children}
      </main>
    </div>
  );
};